import {
  ApolloClient,
  split,
  fromPromise,
  NormalizedCacheObject,
  type ServerError,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { createClient } from 'graphql-ws';
import { AccountApi } from '@/api/account.api';
import { eventsApi, EVENTS } from '@/api/events.api';
import cache from '@/config/chatCache';
// import { EventManager } from '@/core/eventManager';
import { NaluriChatError } from '@/errors';
import { tokenStore, AuthTokens } from '@/utils/tokenStore';
import { wsUrl } from '@/utils/wsURL';
import version from './version';

const UNAUTHENTICATED = 'UNAUTHENTICATED';

export class WsClient {
  private apolloClient: ApolloClient<NormalizedCacheObject>;
  constructor(
    private readonly accountApi: AccountApi,
    private readonly onRefreshTokenError?: () => Promise<void>,
    private readonly refreshTokens?: () => Promise<AuthTokens>
  ) {
    this.createClient();
    this.refreshTokens = async () => {
      const refreshToken = tokenStore.getItem('refreshToken');
      try {
        return await this.accountApi.refresh(refreshToken);
      } catch (error) {
        if (eventsApi.isReady())
          eventsApi.track(EVENTS.REFRESH_ERROR, {
            error,
            action: '@chatApiInstance::refreshTokens',
          });

        if (this.onRefreshTokenError) {
          await this.onRefreshTokenError();
        }

        throw new NaluriChatError({
          message: 'Failed to refresh token',
          code: '4009',
        });
      }
    };
  }

  public instance() {
    return this.apolloClient;
  }

  private _authLink(accessToken) {
    return setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${accessToken}`,
          'x-api-key': this.accountApi.getApiKey(),
        },
      };
    });
  }

  private _websocketLink(accessToken) {
    return new GraphQLWsLink(
      createClient({
        url: `${wsUrl(this.accountApi.getApiUrl())}/graphql`,
        connectionParams: {
          authorization: `Bearer ${accessToken}`,
          'x-api-key': this.accountApi.getApiKey(),
        },
        shouldRetry: () => true,
        retryAttempts: Infinity,
        keepAlive: 10000, // 10 seconds
      })
    );
  }

  private _terminatingLink(webSocketLink, httpUploadLink) {
    return split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      webSocketLink,
      httpUploadLink
    );
  }

  private _uploadhttpLink() {
    return createUploadLink({
      uri: `${this.accountApi.getApiUrl()}/graphql`,
    });
  }

  private _errorLink = () => {
    return onError(({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors)
        return this._handleGraphQLErrors(graphQLErrors, operation, forward);
      if (networkError) {
        return this._handleNetworkError(networkError, operation, forward);
      }
      return forward(operation);
    });
  };

  private _handleGraphQLErrors(errors, operation, forward) {
    errors.forEach(({ message, locations, path, extensions }) => {
      console.debug(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations
        )}, Path: ${path}, Extension: ${extensions}`
      );

      if (extensions?.code === UNAUTHENTICATED) {
        // this.eventManager?.emit('token_expired');

        if (this.refreshTokens) {
          return fromPromise(this.refreshTokens())
            .filter(
              (value) => !!Object.keys(value).length && !!value?.accessToken
            )
            .flatMap((value) => {
              const oldHeaders = operation.getContext().headers;

              operation.setContext({
                headers: {
                  ...oldHeaders,
                  authorization: `Bearer ${value.accessToken}`,
                },
              });

              this.refreshClient(value.accessToken);

              return forward(operation);
            });
        }
      }
      return forward(operation);
    });
  }

  private _handleNetworkError(error, operation, forward) {
    console.log(`[Network error]: ${error}`);
    if (error.name === 'ServerError') {
      const serverError = error as ServerError;

      if (serverError.response.status === 401) {
        if (this.refreshTokens) {
          return fromPromise(this.refreshTokens())
            .filter(
              (value) => !!Object.keys(value).length && !!value?.accessToken
            )
            .flatMap((value) => {
              const oldHeaders = operation.getContext().headers;

              operation.setContext({
                headers: {
                  ...oldHeaders,
                  authorization: `Bearer ${value.accessToken}`,
                },
              });

              this.refreshClient(value.accessToken);

              return forward(operation);
            });
        }
      }
    }
    return forward(operation);
  }

  public refreshClient(accessToken: string) {
    tokenStore.setItem('accessToken', accessToken);
    this.createClient();
  }

  public createClient(): void {
    const accessToken = tokenStore.getItem('accessToken');
    const authLink = this._authLink(accessToken);
    const wsLink = this._websocketLink(accessToken);
    const uploadHttpLink = this._uploadhttpLink();

    const errorLink = this._errorLink();
    const httpUploadLink = errorLink.concat(
      authLink.concat(uploadHttpLink as any)
    );

    const terminatingLink = this._terminatingLink(wsLink, httpUploadLink);

    if (this.apolloClient) this.apolloClient.stop();

    this.apolloClient = new ApolloClient({
      link: terminatingLink,
      cache: cache,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-api-key': this.accountApi.getApiKey(),
        'User-Agent': `Naluri Chat SDK /JS (${version}})`,
        'x-nl-sdk-version': version,
      },
      defaultOptions: {
        watchQuery: {
          refetchWritePolicy: 'merge',
        },
      },
    });
  }
}
